import { FC, useEffect, useState } from 'react';
import { Play, Search, Upload } from 'react-feather';
import styled from 'styled-components';
import { AssetsResponse } from '../../../../../types/assets.types';
import { getAssetDisplayName } from '../../../../../util/assetDisplayName';
import { getStaticFileUrl } from '../../../../../util/getStaticFileUrl';
import { AddAssetForm } from '../../../../../containers/assets/AddAssetForm';
import { DialogModal } from '../../../../DialogModal';
import { editorTheme } from '../../../theme';
import Button from '../components/Button';
import { MediaLibrary } from './useMediaLibrary';

const SEARCH_DELAY = 300;
const PLACEHOLDER_TILES = 6;

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SearchField = styled.label`
  height: 36px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 10px;
  border: 1px solid ${editorTheme.border};
  border-radius: 8px;
  background: ${editorTheme.surface};
  color: ${editorTheme.textMuted};
  &:focus-within {
    border-color: ${editorTheme.accent};
  }
  & input {
    flex: 1;
    min-width: 0;
    border: none;
    outline: none;
    background: transparent;
    font-size: 13px;
    color: ${editorTheme.text};
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px 10px;
`;

const Tile = styled.button`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
  padding: 0;
  background: none;
  text-align: left;
  &:hover > span:first-child,
  &:focus-visible > span:first-child {
    border-color: ${editorTheme.accent};
  }
  &:focus-visible {
    outline: none;
  }
`;

// The shape of the thumbnails the server makes (300 x 200).
const Thumb = styled.span`
  position: relative;
  display: flex;
  aspect-ratio: 3 / 2;
  border: 1px solid ${editorTheme.border};
  border-radius: 8px;
  background: ${editorTheme.surfaceMuted};
  overflow: hidden;
  & img,
  & video {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

const Placeholder = styled(Thumb)`
  border-color: transparent;
`;

const Badge = styled.span`
  position: absolute;
  left: 6px;
  bottom: 6px;
  display: flex;
  align-items: center;
  gap: 3px;
  padding: 2px 6px;
  border-radius: 10px;
  background: rgba(20, 16, 30, 0.7);
  color: white;
  font-size: 10px;
  font-weight: 600;
`;

const Name = styled.span`
  font-size: 12px;
  line-height: 16px;
  color: ${editorTheme.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Note = styled.p`
  margin: 8px 0;
  font-size: 12px;
  line-height: 18px;
  color: ${editorTheme.textMuted};
`;

interface MediaGridProps {
  kind: 'image' | 'video';
  library: MediaLibrary;
  onPick: (asset: AssetsResponse) => void;
}

const MediaGrid: FC<MediaGridProps> = ({ kind, library, onPick }) => {
  const [searchText, setSearchText] = useState(library.search);
  const [uploading, setUploading] = useState(false);
  const noun = kind === 'image' ? 'images' : 'videos';

  useEffect(() => {
    const timer = setTimeout(
      () => library.setSearch(searchText.trim()),
      SEARCH_DELAY,
    );
    return () => clearTimeout(timer);
  }, [searchText]);

  return (
    <Stack>
      <Button secondary onClick={() => setUploading(true)}>
        <Upload size={15} style={{ marginRight: 8 }} aria-hidden="true" />
        Upload {noun}
      </Button>
      <SearchField>
        <Search size={15} aria-hidden="true" />
        <input
          type="search"
          value={searchText}
          placeholder={`Search ${noun}`}
          aria-label={`Search ${noun}`}
          onChange={(event) => setSearchText(event.target.value)}
        />
      </SearchField>
      {library.isLoading && (
        <Grid aria-busy="true" aria-label={`Loading ${noun}`}>
          {Array.from({ length: PLACEHOLDER_TILES }, (_, index) => (
            <Placeholder key={index} />
          ))}
        </Grid>
      )}
      {!library.isLoading && !library.items.length && (
        <Note>
          {library.search
            ? `No ${noun} match "${library.search}".`
            : `No ${noun} yet. Upload one to use it on your slides.`}
        </Note>
      )}
      {!!library.items.length && (
        <Grid>
          {library.items.map((asset) => {
            const name = getAssetDisplayName(asset);
            return (
              <Tile
                key={asset._id}
                type="button"
                title={name}
                aria-label={`Add ${name} to the slide`}
                onClick={() => onPick(asset)}
              >
                <Thumb>
                  {/* A <video> per asset fetches metadata for every video. */}
                  {kind === 'image' || asset.thumbnailUrl ? (
                    <img
                      src={asset.thumbnailUrl || asset.url}
                      alt=""
                      loading="lazy"
                    />
                  ) : (
                    <video
                      src={getStaticFileUrl(asset.path)}
                      preload="metadata"
                      muted
                    />
                  )}
                  {kind === 'video' && (
                    <Badge aria-hidden="true">
                      <Play size={9} fill="currentColor" />
                      Video
                    </Badge>
                  )}
                </Thumb>
                <Name>{name}</Name>
              </Tile>
            );
          })}
        </Grid>
      )}
      {library.hasMore && (
        <Button
          secondary
          disabled={library.isLoadingMore}
          onClick={library.loadMore}
        >
          {library.isLoadingMore ? 'Loading…' : `Load more ${noun}`}
        </Button>
      )}
      <DialogModal
        title="Add asset"
        onClose={() => setUploading(false)}
        open={uploading}
      >
        <AddAssetForm
          onFinished={() => {
            setUploading(false);
            library.refresh();
          }}
        />
      </DialogModal>
    </Stack>
  );
};

export default MediaGrid;
